import React, { useMemo, useState } from "react";
import {
    alpha,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { FaCheck, FaGem, FaRocket, FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { SectionHeading } from "./utils";

/* ────────────────── Styled bits ────────────────── */

const BaseCard = styled(Card)(({ theme }) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 28,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
    background: theme.palette.mode === "light"
        ? `linear-gradient(165deg, ${alpha("#ffffff", 0.96)} 0%, ${alpha("#f8faff", 0.92)} 100%)`
        : `linear-gradient(165deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    transition: "transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease",
    boxShadow: "0 20px 42px rgba(35, 40, 79, 0.12)",
    backdropFilter: "blur(16px)",
    "&:hover": {
        transform: "translateY(-10px)",
        boxShadow: "0 26px 64px rgba(79,70,229,0.24)",
        borderColor: alpha(theme.palette.primary.main, 0.4),
    },
}));

const FeaturedCard = styled(BaseCard)(({ theme }) => ({
    position: "relative",
    boxShadow: "0 26px 66px rgba(85,72,241,0.3)",
    background:
        theme.palette.mode === "light"
            ? `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#eef1ff", 0.95)} 100%)`
            : `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.action.selected} 100%)`,
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: 28,
        padding: 1.2,
        background: "linear-gradient(125deg, #6366f1 5%, #8b5cf6 50%, #22d3ee 95%)",
        WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
        pointerEvents: "none",
    },
}));

const IconRing = styled("div")(({ theme, color = theme.palette.primary.main }) => ({
    display: "grid",
    placeItems: "center",
    width: 68,
    height: 68,
    marginInline: "auto",
    borderRadius: 24,
    color,
    background: `radial-gradient(circle at 25% 20%, ${alpha("#ffffff", 0.85)} 0%, ${alpha(color, 0.23)} 100%)`,
    boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.65)}, 0 12px 24px ${alpha(color, 0.28)}`,
}));

/* ────────────────── Helpers ────────────────── */

const formatKES = (n) =>
    typeof n === "number" ? `Kes ${n.toLocaleString()}` : n;

function PriceBlock({ plan, billingFrequency }) {
    if (plan.monthlyPrice === "Free") {
        return (
            <Typography variant="h3" component="p" sx={{ mt: 1, mb: 2 }}>
                Free
            </Typography>
        );
    }
    const price =
        billingFrequency === "monthly" ? plan.monthlyPrice : plan.annualPrice;
    return (
        <Stack alignItems="center" spacing={0.5} sx={{ mt: 1, mb: 2 }}>
            <Typography variant="h3" component="p">
                {formatKES(price)}
                <Typography
                    variant="subtitle1"
                    component="span"
                    sx={{ ml: 1, color: "text.secondary" }}
                >
                    /{billingFrequency === "monthly" ? "mo" : "yr"}
                </Typography>
            </Typography>
            {billingFrequency === "annual" && typeof plan.monthlyPrice === "number" && typeof plan.annualPrice === "number" ? (
                <Typography variant="caption" color="text.secondary">
                    Billed annually
                </Typography>
            ) : null}
        </Stack>
    );
}

/* ────────────────── Data ────────────────── */

const UNIVERSAL_QR_FEATURES = [
    "Dynamic QR Codes",
    "Link shorteners",
    "Survey QR Codes",
    "Discount codes",
    "QR Landing page",
    "Encrypted QR Codes",
    "Scan analytics",
    "QR Customisations",
];

const FREE_TRIAL = [
    {
        key: "free",
        title: "Free Trial",
        monthlyPrice: "Free",
        annualPrice: "Free",
        features: ["Automatic two-week trial", "Unlimited scans", "Up to 10 Users", "100MB Storage", "24/7 Support", ...UNIVERSAL_QR_FEATURES],
        icon: <FaStar size={22} />,
        color: "#2196f3",
    },
];

const PLANS = [
    {
        key: "pro",
        title: "Professional",
        monthlyPrice: 2000,
        annualPrice: 20000,
        features: [
            "Up to 10 Users",
            "Up to 100,000 Scans",
            "50 Uploads",
            "200MB Storage",
            "Priority Support",
            ...UNIVERSAL_QR_FEATURES,
        ],
        icon: <FaGem size={22} />,
        color: "#9c27b0",
        popular: true,
    },
    {
        key: "ent",
        title: "Enterprise",
        monthlyPrice: 4000,
        annualPrice: 40000,
        features: [
            "Unlimited Users",
            "Unlimited Scans",
            "500MB Storage",
            "24/7 Support",
            "Custom Integration",
            "Dedicated Manager",
            ...UNIVERSAL_QR_FEATURES,
        ],
        icon: <FaRocket size={22} />,
        color: "#f50057",
    },
];

/* ────────────────── Component ────────────────── */

export default function PricingSection() {
    const [billingFrequency, setBillingFrequency] = useState("monthly");
    const navigate = useNavigate();

    const handleBillingChange = (_e, v) => v && setBillingFrequency(v);
    const handleGetStarted = (planKey) => {
        navigate("/auth", { state: { selectedPlan: planKey, billingFrequency } });
    };
    const handleTalkToSales = () => {
        const contactSection = document.getElementById("contact");
        if (contactSection) {
            contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
        }
        navigate("/?scroll=contact");
    };
    const handleComparePlans = () => {
        document.getElementById("pricing-cards")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Keep featured plan centered on larger screens
    const orderedPlans = useMemo(() => {
        const featured = PLANS.find((p) => p.popular);
        const others = PLANS.filter((p) => !p.popular);
        return featured ? [others[0], featured, others[1]].filter(Boolean) : PLANS;
    }, []);

    return (
        <Container
            id="pricing"
            maxWidth="lg"
            sx={{
                py: { xs: 6, sm: 8, md: 10 },
                px: { xs: 2, sm: 3, md: 4 },
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    width: { xs: 220, md: 320 },
                    height: { xs: 220, md: 320 },
                    borderRadius: "50%",
                    top: { xs: 10, md: -20 },
                    left: { xs: -80, md: -120 },
                    background: "radial-gradient(circle, rgba(99,102,241,0.18), rgba(99,102,241,0))",
                    pointerEvents: "none",
                },
            }}
        >
            <Box textAlign="center" mb={{ xs: 4, sm: 6 }}>
                <SectionHeading>Choose Your Plan</SectionHeading>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Simple pricing. Powerful QR features on every package. Upgrade, renew, or cancel anytime.
                </Typography>

                <ToggleButtonGroup
                    value={billingFrequency}
                    exclusive
                    onChange={handleBillingChange}
                    aria-label="billing frequency"
                    sx={{
                        mt: 3,
                        p: 0.5,
                        borderRadius: 999,
                        background: (theme) => alpha(theme.palette.primary.main, 0.08),
                        "& .MuiToggleButton-root": {
                            px: 2.5,
                            py: 1,
                            fontWeight: 700,
                            borderRadius: 999,
                            border: "none",
                            color: "text.secondary",
                            "&.Mui-selected": {
                                color: "text.primary",
                                background: "linear-gradient(120deg, #e2e8ff 0%, #edf8ff 100%)",
                                boxShadow: "0 4px 12px rgba(99,102,241,0.18)",
                            },
                        },
                    }}
                >
                    <ToggleButton value="monthly" aria-label="monthly billing">
                        Monthly
                    </ToggleButton>
                    <ToggleButton value="annual" aria-label="annual billing">
                        Annual <Box component="span" sx={{ ml: 1, opacity: 0.7 }}>(save ~20%)</Box>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Grid
                id="pricing-cards"
                container
                rowSpacing={{ xs: 2, sm: 3, md: 4 }}
                columnSpacing={{ xs: 2, sm: 3, md: 4 }}
                alignItems="stretch"
            >
                {/* Free Trial – automatic two-week card with no price math */}
                {FREE_TRIAL.map((plan) => (
                    <Grid key={plan.key} item xs={12} sm={6} md={4}>
                        <BaseCard>
                            <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                                <IconRing color={plan.color} aria-hidden="true">
                                    {plan.icon}
                                </IconRing>

                                <Typography
                                    component="h3"
                                    sx={{ mt: 2, fontWeight: 900, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                                >
                                    {plan.title}
                                </Typography>

                                <PriceBlock plan={plan} billingFrequency={billingFrequency} />

                                <List dense sx={{ mb: 1.5 }}>
                                    {plan.features.map((f) => (
                                        <ListItem key={f} sx={{ justifyContent: "center" }}>
                                            <ListItemIcon sx={{ minWidth: "auto", mr: 1, color: plan.color }}>
                                                <FaCheck size={16} />
                                            </ListItemIcon>
                                            <ListItemText primary={f} primaryTypographyProps={{ variant: "body2" }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                            <Divider sx={{ opacity: 0.7 }} />
                            <CardActions sx={{ p: 2 }}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => handleGetStarted(plan.key)}
                                    sx={{
                                        fontWeight: 800,
                                        borderRadius: 999,
                                        py: 1.1,
                                        borderColor: plan.color,
                                        "&:hover": { backgroundColor: `${plan.color}15`, borderColor: plan.color },
                                    }}
                                >
                                    Start Two-Week Trial
                                </Button>
                            </CardActions>
                        </BaseCard>
                    </Grid>
                ))}

                {/* Paid Plans */}
                {orderedPlans.map((plan) => {
                    const CardComp = plan.popular ? FeaturedCard : BaseCard;
                    return (
                        <Grid key={plan.key} item xs={12} sm={6} md={4}>
                            <CardComp>
                                {/* Featured badge */}
                                {plan.popular && (
                                    <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
                                        <Chip size="small" color="primary" label="Most Popular" sx={{ fontWeight: 700, borderRadius: 999, px: 0.8 }} />
                                    </Box>
                                )}

                                <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                                    <IconRing color={plan.color} aria-hidden="true">
                                        {plan.icon}
                                    </IconRing>

                                    <Typography
                                        component="h3"
                                        sx={{ mt: 2, fontWeight: 900, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                                    >
                                        {plan.title}
                                    </Typography>

                                    <PriceBlock plan={plan} billingFrequency={billingFrequency} />

                                    <List dense sx={{ mb: 1.5 }}>
                                        {plan.features.map((f) => (
                                            <ListItem key={f} sx={{ justifyContent: "center" }}>
                                                <ListItemIcon sx={{ minWidth: "auto", mr: 1, color: plan.color }}>
                                                    <FaCheck size={16} />
                                                </ListItemIcon>
                                                <ListItemText primary={f} primaryTypographyProps={{ variant: "body2" }} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>

                                <Divider sx={{ opacity: 0.7 }} />
                                <CardActions
                                    sx={{
                                        p: 2,
                                        gap: 1,
                                        gridTemplateColumns: plan.popular ? "1fr 1fr" : "1fr",
                                        display: "grid",
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        variant={plan.popular ? "contained" : "outlined"}
                                        onClick={() => handleGetStarted(plan.key)}
                                        sx={{
                                            fontWeight: 800,
                                            borderRadius: 999,
                                            ...(plan.popular
                                                ? {
                                                    background: "linear-gradient(90deg, #6366f1 0%, #8b5cf6 55%, #06b6d4 100%)",
                                                    boxShadow: "0 10px 26px rgba(99,102,241,0.38)",
                                                    "&:hover": { filter: "brightness(0.98)" },
                                                }
                                                : { borderColor: plan.color, "&:hover": { backgroundColor: `${plan.color}15`, borderColor: plan.color } }),
                                        }}
                                    >
                                        Get Started
                                    </Button>

                                    {plan.popular && (
                                        <Button
                                            fullWidth
                                            color="inherit"
                                            variant="text"
                                            onClick={handleTalkToSales}
                                            sx={{ fontWeight: 800, borderRadius: 999 }}
                                        >
                                            Talk to Sales
                                        </Button>
                                    )}
                                </CardActions>
                            </CardComp>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Bottom strip */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{
                    mt: { xs: 5, sm: 6 },
                    p: { xs: 2, sm: 3.2 },
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: { xs: 4, sm: 999 },
                    background: "linear-gradient(110deg, rgba(255,255,255,0.88), rgba(238,242,255,0.72))",
                    textAlign: "center",
                }}
            >
                <Typography color="text.secondary">
                    Need custom SLAs, on-prem, or SSO?
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <Button
                        variant="contained"
                        onClick={handleTalkToSales}
                        sx={{
                            fontWeight: 800,
                            borderRadius: 999,
                            background: "linear-gradient(90deg, #6366f1 0%, #2563eb 100%)",
                            width: { xs: "100%", sm: "auto" },
                        }}
                    >
                        Contact Us
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleComparePlans}
                        sx={{ fontWeight: 800, borderRadius: 999, width: { xs: "100%", sm: "auto" } }}
                    >
                        Compare Plans
                    </Button>
                </Stack>
            </Stack>
        </Container>
    );
}
